import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import { ACTION_LABELS, MODULE_LABELS } from "@/store/permissions/catalog";
import { useRoles } from "@/store/permissions/RolesProvider";
import { useEmployes } from "./EmployesProvider";
import EmployeForm from "./components/EmployeForm";
import {
  EMPLOYE_STATUT_ICONS,
  EMPLOYE_STATUT_LABELS,
  EMPLOYE_STATUT_OPTIONS,
  EMPLOYE_STATUT_TONES,
  employeInitiales,
  employeNomComplet,
  employeRoleLabel,
  type Employe,
  type EmployeStatut,
} from "./types";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

function describePermissions(employe: Employe): string {
  return Object.keys(employe.permissions)
    .filter((module) => Object.values(employe.permissions[module] ?? {}).some(Boolean))
    .map((module) => {
      const actions = Object.entries(employe.permissions[module] ?? {})
        .filter(([, value]) => value)
        .map(([action]) => ACTION_LABELS[action as keyof typeof ACTION_LABELS])
        .join("/");
      return `${MODULE_LABELS[module] ?? module} (${actions})`;
    })
    .join(", ");
}

export default function AgenceEmployesPage() {
  const { employes, currentEmployeId, setCurrentEmployeId, can, updateStatut, resendInvitation } = useEmployes();
  const { roles, roleOptions } = useRoles();
  const location = useLocation();
  const isOwner = location.pathname.startsWith("/proprietaire");
  const base = isOwner ? "/proprietaire/employes" : "/agence/employes";

  // Libellé de rôle : nom configuré en base, sinon libellé du catalogue (anciens codes).
  const roleName = (code: string) => roles.find((role) => role.code === code)?.nom ?? employeRoleLabel(code);

  const roleFilterOptions = useMemo(() => {
    const options = new Map<string, string>();
    roleOptions.forEach((option) => options.set(option.value, option.label));
    employes.forEach((employe) => {
      if (employe.role && !options.has(employe.role)) options.set(employe.role, employeRoleLabel(employe.role));
    });
    return [{ value: "", label: "Tous les rôles" }, ...Array.from(options, ([value, label]) => ({ value, label }))];
  }, [roleOptions, employes]);

  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const peutAjouter = can("employes.creer");
  const peutModifier = can("employes.modifier");

  const counts = useMemo(
    () => ({
      actifs: employes.filter((employe) => employe.statut === "actif").length,
      invites: employes.filter((employe) => employe.statut === "invite").length,
      inactifs: employes.filter((employe) => employe.statut === "inactif").length,
    }),
    [employes]
  );

  const profileOptions = useMemo(
    () => [
      { value: "", label: "Accès complet (responsable)" },
      ...employes.map((employe) => ({ value: employe.id, label: employeNomComplet(employe) })),
    ],
    [employes]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = employes.filter((employe) => {
      const matchesSearch =
        !term ||
        [employe.prenom, employe.nom, employe.email, employe.telephone, roleName(employe.role)]
          .join(" ")
          .toLowerCase()
          .includes(term);
      const matchesRole = !roleFilter || employe.role === roleFilter;
      const matchesStatut = !statutFilter || employe.statut === statutFilter;
      return matchesSearch && matchesRole && matchesStatut;
    });

    return result.sort((a, b) => {
      if (sort === "nom") return employeNomComplet(a).localeCompare(employeNomComplet(b), "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [employes, search, roleFilter, statutFilter, sort]);

  const isFiltering = Boolean(search.trim()) || Boolean(roleFilter) || Boolean(statutFilter);

  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStatutFilter("");
  };

  const exportColumns: ExportColumn<Employe>[] = [
    { header: "Nom complet", value: (employe) => employeNomComplet(employe) },
    { header: "Rôle", value: (employe) => roleName(employe.role) },
    { header: "Email", value: (employe) => employe.email },
    { header: "Téléphone", value: (employe) => employe.telephone },
    { header: "Statut", value: (employe) => EMPLOYE_STATUT_LABELS[employe.statut] },
    { header: "Permissions", value: (employe) => describePermissions(employe) },
    { header: "Créé le", value: (employe) => formatDate(employe.createdAt) },
  ];

  const columns: DataTableColumn<Employe>[] = [
    {
      key: "identite",
      header: "Employé",
      className: "min-w-[200px]",
      render: (employe) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-xs font-semibold text-primary-800">
            {employeInitiales(employe)}
          </span>
          <span className="truncate font-label font-medium text-foreground-950">{employeNomComplet(employe)}</span>
        </div>
      ),
    },
    {
      key: "role",
      header: "Rôle",
      className: "min-w-[150px]",
      render: (employe) => <span className="text-foreground-700">{roleName(employe.role)}</span>,
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[220px]",
      render: (employe) => <span className="text-foreground-700">{employe.email || "Non renseigné"}</span>,
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[150px]",
      render: (employe) => <span className="text-foreground-700">{employe.telephone || "Non renseigné"}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (employe) => (
        <Badge tone={EMPLOYE_STATUT_TONES[employe.statut]} icon={EMPLOYE_STATUT_ICONS[employe.statut]}>
          {EMPLOYE_STATUT_LABELS[employe.statut]}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[250px]",
      render: (employe) => (
        <div className="flex items-center justify-end gap-2">
          {peutModifier && (
            <div className="relative">
              <select
                aria-label="Changer le statut"
                value={employe.statut}
                onChange={(event) => updateStatut(employe.id, event.target.value as EmployeStatut)}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 py-2 pl-3 pr-8 text-xs text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/60"
              >
                {EMPLOYE_STATUT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i
                className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              />
            </div>
          )}
          {peutModifier && employe.statut === "invite" && (
            <button
              type="button"
              onClick={() => resendInvitation(employe.id)}
              title="Renvoyer l'invitation"
              aria-label="Renvoyer l'invitation"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-mail-send-line text-base" aria-hidden="true" />
            </button>
          )}
          <Link
            to={`${base}/${employe.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir la fiche
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Employés</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            {isOwner
              ? "Gérez les membres de votre équipe et leurs permissions par module. Chaque employé voit uniquement les modules et actions que vous autorisez."
              : "Gérez l'équipe et attribuez à chaque employé les permissions par module qui déterminent son menu et ses actions."}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="employes" title="Employés" />
          {peutAjouter && (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-user-add-line text-base" aria-hidden="true" />
              Ajouter un employé
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-team-line" label="Employés" value={String(employes.length)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(counts.actifs)} tone="secondary" />
        <StatCard icon="ri-mail-send-line" label="Invités" value={String(counts.invites)} tone="accent" />
        <StatCard icon="ri-pause-circle-line" label="Inactifs" value={String(counts.inactifs)} tone="neutral" />
      </div>

      {employes.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-3xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="emp-search" className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input id="emp-search" icon="ri-search-line" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, email, téléphone..." />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="emp-role-filter" className="text-xs font-label font-medium text-foreground-700">
                  Rôle
                </label>
                <Select id="emp-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} options={roleFilterOptions} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="emp-statut-filter" className="text-xs font-label font-medium text-foreground-700">
                  Statut
                </label>
                <Select id="emp-statut-filter" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)} options={[{ value: "", label: "Tous les statuts" }, ...EMPLOYE_STATUT_OPTIONS]} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor="emp-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select id="emp-sort" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} options={sortOptions} />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} employé{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {employes.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {employes.length > 0 && (
        <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-user-follow-line text-base" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-label font-medium text-foreground-950">Prévisualiser un profil de permissions</p>
              <p className="mt-0.5 text-xs text-foreground-500">
                Affichez l'espace tel qu'il apparaît pour un employé : le menu et les actions s'adaptent à ses permissions.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 md:shrink-0">
            <Select
              aria-label="Profil de permissions"
              value={currentEmployeId ?? ""}
              onChange={(event) => setCurrentEmployeId(event.target.value || null)}
              options={profileOptions}
              className="md:w-64"
            />
            {currentEmployeId && (
              <button
                type="button"
                onClick={() => setCurrentEmployeId(null)}
                className={cn(
                  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2.5 text-xs font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
                )}
              >
                <i className="ri-arrow-go-back-line text-sm" aria-hidden="true" />
                Accès complet
              </button>
            )}
          </div>
        </section>
      )}

      {employes.length === 0 && (
        <EmptyState
          icon="ri-team-line"
          title="Aucun employé pour le moment"
          description="Ajoutez les membres de votre équipe et attribuez-leur des permissions par module. Chaque employé verra uniquement ce que vous lui autorisez."
          action={
            peutAjouter ? (
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-user-add-line text-base" aria-hidden="true" />
                Ajouter un employé
              </button>
            ) : undefined
          }
        />
      )}

      {employes.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun employé ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(employe) => employe.id}
          minWidthClass="min-w-[980px]"
          caption="Employés"
        />
      )}

      <EmployeForm open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}